import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ClockIcon,
  TrophyIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../../components/navBar";

const LiveMatches = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [players, setPlayers] = useState({});
  const [elapsedTimes, setElapsedTimes] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );

  useEffect(() => {
    fetchLiveMatches();
    const interval = setInterval(fetchLiveMatches, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateElapsedTimes = () => {
      const currentTime = new Date();
      const updatedTimes = {};

      liveMatches.forEach((match) => {
        if (!match?.startTime) return;
        const matchStartTime = new Date(match.startTime);
        const matchEndTime = new Date(matchStartTime.getTime() + 105 * 60000);

        if (currentTime >= matchStartTime && currentTime <= matchEndTime) {
          const diff = currentTime - matchStartTime;
          let elapsedMinutes = Math.floor(diff / 60000);
          if (elapsedMinutes > 45 && elapsedMinutes < 60) {
            elapsedMinutes = 45;
          } else if (elapsedMinutes >= 60) {
            elapsedMinutes = Math.min(Math.max(0, elapsedMinutes - 15), 90);
          }
          updatedTimes[match.matchesId] = elapsedMinutes;
        } else if (currentTime > matchEndTime) {
          updatedTimes[match.matchesId] = "FT";
        } else {
          updatedTimes[match.matchesId] = "";
        }
      });

      setElapsedTimes(updatedTimes);
    };

    updateElapsedTimes();
    const timer = setInterval(updateElapsedTimes, 60000);
    return () => clearInterval(timer);
  }, [liveMatches]);

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  useEffect(() => {
    if (!selectedMatch) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedMatch(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedMatch]);

  const fetchLiveMatches = async () => {
    try {
      const [matchesResponse, teamsResponse, leaguesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/matches`),
        fetch(`${process.env.REACT_APP_API_URL}/clubs`),
        fetch(`${process.env.REACT_APP_API_URL}/leagues`),
      ]);

      if (!matchesResponse.ok || !teamsResponse.ok || !leaguesResponse.ok) {
        throw new Error("Sports data is temporarily unavailable.");
      }

      const [matches, teams, leagues] = await Promise.all([
        matchesResponse.json(),
        teamsResponse.json(),
        leaguesResponse.json(),
      ]);
      const matchesArray = Array.isArray(matches) ? matches : matches.data || [];
      const teamsArray = Array.isArray(teams) ? teams : teams.data || [];
      const leaguesArray = Array.isArray(leagues) ? leagues : leagues.data || [];

      const teamsLookup = teamsArray.reduce((acc, team) => {
        acc[team.clubId] = { name: team.name, logo: team.logo };
        return acc;
      }, {});
      const leaguesLookup = leaguesArray.reduce((acc, league) => {
        acc[league.leagueId] = league.name;
        return acc;
      }, {});

      const currentDate = new Date();
      const liveMatchesData = matchesArray
        .filter((match) => {
          const matchDate = new Date(match.date);
          const matchEndTime = new Date(matchDate.getTime() + 105 * 60000);
          return !Number.isNaN(matchDate.getTime()) && matchDate <= currentDate && currentDate <= matchEndTime;
        })
        .map((match) => {
          const matchDate = new Date(match.date);
          return {
            id: match.matchesId,
            time: matchDate.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
            displayDate: matchDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
            startTime: matchDate,
            leagueName: leaguesLookup[match.leagueId],
            homeTeam: teamsLookup[match.homeTeamId],
            awayTeam: teamsLookup[match.awayTeamId],
            homeTeamId: match.homeTeamId,
            awayTeamId: match.awayTeamId,
            homeTeamScore: match.homeTeamScore,
            awayTeamScore: match.awayTeamScore,
            matchesId: match.matchesId,
          };
        });

      setLiveMatches(liveMatchesData);
      await Promise.all(liveMatchesData.map((match) => fetchScores(match.matchesId)));
      setLastUpdated(new Date());
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching live matches:", err);
      setError(err.message || "Unable to retrieve live match data.");
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLiveMatches();
    setIsRefreshing(false);
  };

  const fetchScores = async (matchId) => {
    if (scores[matchId]) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/scores/matches/${matchId}/scores`
      );
      setScores((prev) => ({ ...prev, [matchId]: response.data }));
      await Promise.all(response.data.map((score) => fetchPlayerName(score.playerId)));
    } catch (scoreError) {
      console.error("Error fetching scores:", scoreError);
      toast.error("Failed to fetch scores.");
    }
  };

  const fetchPlayerName = async (playerId) => {
    if (!playerId) return null;
    if (players[playerId]) return players[playerId];

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/players/${playerId}`);
      const playerName = response.data.name;
      setPlayers((prev) => ({ ...prev, [playerId]: playerName }));
      return playerName;
    } catch (playerError) {
      console.error("Error fetching player name:", playerError);
      return "Unknown Player";
    }
  };

  const fetchMatchDetails = async (match) => {
    setSelectedMatch(match);
    if (!scores[match.matchesId]) {
      await fetchScores(match.matchesId);
    }
    if (scores[match.matchesId]) {
      await Promise.all(
        scores[match.matchesId].map((score) => fetchPlayerName(score.playerId))
      );
    }
  };

  const groupedMatches = liveMatches.reduce((groups, match) => {
    const competition = match.leagueName || "Competition unavailable";
    if (!groups[competition]) groups[competition] = [];
    groups[competition].push(match);
    return groups;
  }, {});
  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";

  if (loading) {
    return (
      <>
        <Navbar />
        <main
          style={{ "--live-sidebar-width": sidebarWidth }}
          className="min-h-screen bg-surface-950 px-5 pb-10 pt-24 text-white md:ml-64 md:pt-20 lg:ml-[var(--live-sidebar-width)] sm:px-8"
        >
          <div role="status" aria-label="Loading live matches" className="mx-auto flex min-h-72 max-w-7xl items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
            <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
            Loading live match data…
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main
        style={{ "--live-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--live-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div>
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <VideoCameraIcon aria-hidden="true" className="h-4 w-4" />
                MATCH CENTRE
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Live matches</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Follow ongoing matches and the latest available scores.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div aria-live="polite" className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${liveMatches.length ? "animate-pulse bg-accent" : "bg-gray-500"}`} />
                {liveMatches.length} {liveMatches.length === 1 ? "match" : "matches"} in progress
              </div>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line-strong bg-surface-800 px-3 text-sm font-medium text-gray-200 transition-colors hover:bg-surface-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60"
              >
                <ArrowPathIcon aria-hidden="true" className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                {isRefreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
            <p>Scores refresh automatically every minute.</p>
            {lastUpdated && (
              <p className="inline-flex items-center gap-1.5">
                <ClockIcon aria-hidden="true" className="h-4 w-4" />
                Updated <time dateTime={lastUpdated.toISOString()}>{lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
              </p>
            )}
          </div>

          {error && (
            <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm text-gray-300 sm:flex-row sm:items-center sm:justify-between">
              <p>{error} {liveMatches.length > 0 ? "Showing the most recently loaded matches." : ""}</p>
              <button type="button" onClick={handleRefresh} disabled={isRefreshing} className="btn-secondary min-h-10 shrink-0 gap-2">
                <ArrowPathIcon aria-hidden="true" className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                Try again
              </button>
            </div>
          )}

          {liveMatches.length === 0 ? (
            <section className="flex min-h-72 flex-col items-center justify-center rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-surface-850">
                {error ? <VideoCameraIcon aria-hidden="true" className="h-5 w-5 text-gray-500" /> : <ClockIcon aria-hidden="true" className="h-5 w-5 text-gray-500" />}
              </span>
              <h2 className="mt-4 text-lg font-semibold text-white">
                {error ? "Live matches are unavailable" : "No matches in progress"}
              </h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-gray-400">
                {error
                  ? "We couldn't retrieve the latest match data. Try refreshing in a moment."
                  : "There are no live matches at the moment. Check back later or browse upcoming fixtures."}
              </p>
              {!error && (
                <a href="/upcoming-matches" className="mt-5 inline-flex min-h-10 items-center gap-2 rounded-lg border border-line-strong bg-surface-800 px-4 text-sm font-semibold text-gray-200 transition-colors hover:bg-surface-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                  Upcoming matches <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                </a>
              )}
            </section>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedMatches).map(([competition, matches]) => (
                <section key={competition} aria-label={`${competition} live matches`}>
                  <div className="mb-3 flex items-center gap-2">
                    <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                    <h2 className="text-sm font-semibold text-gray-200">{competition}</h2>
                    <span className="rounded-md border border-line bg-surface-900 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                      {matches.length} {matches.length === 1 ? "match" : "matches"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {matches.map((match) => {
                      const matchScores = Array.isArray(scores[match.matchesId]) ? scores[match.matchesId] : null;
                      const homeGoals = matchScores?.filter((score) => score.clubId === match.homeTeamId) || [];
                      const awayGoals = matchScores?.filter((score) => score.clubId === match.awayTeamId) || [];
                      const homeScore = match.homeTeamScore ?? (matchScores ? homeGoals.length : null);
                      const awayScore = match.awayTeamScore ?? (matchScores ? awayGoals.length : null);
                      const elapsed = elapsedTimes[match.matchesId];
                      const isFinished = elapsed === "FT";

                      return (
                        <article key={match.id} className="group overflow-hidden rounded-xl border border-line bg-surface-850 transition-all duration-200 hover:border-line-strong hover:bg-surface-800">
                          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface-900 px-4 py-3 sm:px-5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-bold tracking-[0.12em] ${isFinished ? "border-line bg-surface-800 text-gray-400" : "border-accent/25 bg-accent/10 text-accent-soft"}`}>
                                {!isFinished && <span aria-hidden="true" className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />}
                                {isFinished ? "FULL TIME" : "LIVE"}
                              </span>
                              <time dateTime={match.startTime.toISOString()} className="text-xs text-gray-500">
                                {match.displayDate} · {match.time}
                              </time>
                            </div>
                            <span className={`text-sm font-semibold tabular-nums ${isFinished ? "text-gray-400" : "text-accent-soft"}`}>
                              {typeof elapsed === "number" ? `${elapsed}′` : isFinished ? "FT" : "In progress"}
                            </span>
                          </div>

                          <div className="space-y-4 p-4 sm:p-5">
                            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-4">
                              <TeamRow team={match.homeTeam} score={homeScore} />
                              <TeamRow team={match.awayTeam} score={awayScore} />
                              <span className="col-start-2 row-start-1 text-right text-2xl font-bold tabular-nums text-white sm:text-3xl" aria-label={`Home score ${homeScore ?? "not available"}`}>
                                {homeScore ?? "—"}
                              </span>
                              <span className="col-start-2 row-start-2 text-right text-2xl font-bold tabular-nums text-white sm:text-3xl" aria-label={`Away score ${awayScore ?? "not available"}`}>
                                {awayScore ?? "—"}
                              </span>
                            </div>

                            {(homeGoals.length > 0 || awayGoals.length > 0) && (
                              <div className="grid grid-cols-1 gap-2 rounded-lg border border-line bg-surface-900 px-3 py-3 sm:grid-cols-2 sm:px-4">
                                <GoalList goals={homeGoals} players={players} align="left" />
                                <GoalList goals={awayGoals} players={players} align="right" />
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => fetchMatchDetails(match)}
                              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-line-strong bg-surface-800 px-4 text-sm font-semibold text-gray-200 transition-colors hover:bg-surface-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                              Match details <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        {selectedMatch && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" role="presentation" onClick={() => setSelectedMatch(null)}>
            <section role="dialog" aria-modal="true" aria-labelledby="live-match-details-title" className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-line-strong bg-surface-850 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-line bg-surface-900 p-4 sm:p-6">
                <div>
                  <p className="eyebrow-accent">MATCH DETAILS</p>
                  <h2 id="live-match-details-title" className="mt-1 text-lg font-bold text-white sm:text-xl">{selectedMatch.leagueName || "Competition unavailable"}</h2>
                </div>
                <button type="button" aria-label="Close match details" onClick={() => setSelectedMatch(null)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-surface-700 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
                  <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 sm:p-6">
                <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 rounded-xl border border-line bg-surface-900 p-4 sm:gap-5 sm:p-5">
                  <div className="flex min-w-0 flex-col items-center gap-2 text-center sm:flex-row sm:text-left">
                    <TeamCrest team={selectedMatch.homeTeam} />
                    <span className="text-sm font-medium text-gray-200 sm:text-base">{selectedMatch.homeTeam?.name || "Home team"}</span>
                  </div>
                  <div className="text-center text-xl font-bold tabular-nums text-white sm:text-2xl">
                    {getDisplayScore(selectedMatch, scores)}
                  </div>
                  <div className="flex min-w-0 flex-col items-center gap-2 text-center sm:flex-row sm:justify-end sm:text-right">
                    <span className="text-sm font-medium text-gray-200 sm:text-base">{selectedMatch.awayTeam?.name || "Away team"}</span>
                    <TeamCrest team={selectedMatch.awayTeam} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[selectedMatch.homeTeamId, selectedMatch.awayTeamId].map((clubId, index) => {
                    const team = index === 0 ? selectedMatch.homeTeam : selectedMatch.awayTeam;
                    const teamGoals = (scores[selectedMatch.matchesId] || [])
                      .filter((score) => score.clubId === clubId)
                      .sort((a, b) => a.timeScored - b.timeScored);
                    return (
                      <div key={`${clubId}-${index}`} className="min-w-0 rounded-lg border border-line bg-surface-900 p-4">
                        <h3 className="mb-3 truncate text-sm font-semibold text-gray-200">{team?.name || (index === 0 ? "Home team" : "Away team")}</h3>
                        {teamGoals.length > 0 ? (
                          <div className="space-y-2">
                            {teamGoals.map((score) => (
                              <div key={score.id} className="flex items-start gap-2 text-sm">
                                {score.timeScored != null && <span className="shrink-0 text-accent-soft">{score.timeScored}′</span>}
                                <span className="min-w-0 text-gray-200">{score.playerId ? players[score.playerId] || "Loading player…" : "Unattributed goal"}</span>
                                {(score.isPenalty || score.isOwnGoal) && <span className="shrink-0 text-gray-500">{score.isPenalty ? "(P)" : "(OG)"}</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No recorded goals available.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={() => setSelectedMatch(null)} className="btn-secondary mt-5 min-h-11 w-full">Close details</button>
              </div>
            </section>
          </div>
        )}
      </main>
    </>
  );
};

function getDisplayScore(match, scores) {
  const goals = scores[match.matchesId] || [];
  const hasScoreFeed = Array.isArray(scores[match.matchesId]);
  const homeScore = match.homeTeamScore ?? (hasScoreFeed ? goals.filter((score) => score.clubId === match.homeTeamId).length : null);
  const awayScore = match.awayTeamScore ?? (hasScoreFeed ? goals.filter((score) => score.clubId === match.awayTeamId).length : null);
  return `${homeScore ?? "—"} – ${awayScore ?? "—"}`;
}

function TeamRow({ team, score }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <TeamCrest team={team} />
      <span className="min-w-0 truncate text-sm font-semibold text-gray-200 sm:text-base">
        {team?.name || "Team details unavailable"}
      </span>
      <span className="sr-only">Score: {score ?? "not available"}</span>
    </div>
  );
}

function TeamCrest({ team }) {
  const initials = team?.name
    ? team.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()
    : "?";
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface-800 sm:h-12 sm:w-12">
      {team?.logo ? (
        <img src={team.logo} alt={`${team?.name || "Team"} logo`} className="h-full w-full object-contain p-1.5" />
      ) : (
        <span aria-hidden="true" className="text-xs font-bold text-gray-500">{initials}</span>
      )}
    </span>
  );
}

function GoalList({ goals, players, align }) {
  if (!goals.length) return <div aria-hidden="true" />;
  return (
    <ul className={`space-y-2 ${align === "right" ? "sm:text-right" : ""}`} aria-label={`${align === "left" ? "Home" : "Away"} team goal scorers`}>
      {goals.map((goal, index) => (
        <li key={goal.id || `${goal.playerId}-${goal.timeScored}-${index}`} className={`flex items-start gap-2 text-xs text-gray-400 ${align === "right" ? "sm:justify-end" : ""}`}>
          {goal.timeScored != null && <span className="shrink-0 text-accent-soft">{goal.timeScored}′</span>}
          <span className="min-w-0">{goal.playerId ? players[goal.playerId] || "Loading player…" : "Unattributed goal"}</span>
          {(goal.isPenalty || goal.isOwnGoal) && <span className="shrink-0">{goal.isPenalty ? "(P)" : "(OG)"}</span>}
        </li>
      ))}
    </ul>
  );
}

export default LiveMatches;
